import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, PlayCircle, Image as ImageIcon, Camera, Clock, CalendarDays, MoreHorizontal } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { PageHeader, SectionHeader } from '../components/Shared';
import { api, fileUrl } from '../lib/api';
import { setPageMeta } from '../lib/seo';

// Media hub (Phase 123): Steve's Videos (Two Minutes with Steve / Events /
// Other), Posters (Paper Ballot Series), Photos (Beautiful Ward 8).
export default function MediaHub() {
  const [counts, setCounts] = useState({});
  useEffect(() => {
    setPageMeta({ title: 'Media | Steve Lesperance', description: "Steve's videos, the Paper Ballot poster series and photos of beautiful Ward 8.", url: window.location.origin + '/media' });
    api.get('/videos/library').then((r) => {
      const steve = (r.data?.collections || []).find((c) => c.key === 'steve');
      const byCat = Object.fromEntries((steve?.groups || []).map((g) => [g.key, g.videos.length]));
      setCounts({ videos: steve?.count || 0, ...byCat });
    }).catch(() => {});
  }, []);
  const n = (k) => (counts[k] ? ` (${counts[k]})` : '');

  const GROUPS = [
    { key: 'videos', icon: PlayCircle, title: "Steve's Videos", body: 'Short, straight talk from Steve plus recordings from debates and events.', image: '/api/uploads/vote-steve-hero.webp',
      links: [
        { label: `Two Minutes with Steve${n('two_minutes')}`, url: '/videos?category=two_minutes', icon: Clock, testid: 'media-link-two-minutes' },
        { label: `Events${n('election_events')}`, url: '/videos?category=election_events', icon: CalendarDays, testid: 'media-link-events' },
        { label: `Other${n('other')}`, url: '/videos?category=other', icon: MoreHorizontal, testid: 'media-link-other' },
      ], all: { label: `All of Steve's videos${n('videos')}`, url: '/videos', testid: 'media-link-all-videos' } },
    { key: 'posters', icon: ImageIcon, title: 'Posters', body: 'The Paper Ballot poster series: the case for restoring the paper ballot choice, one poster at a time.', image: '/api/uploads/flyer-qr-poster.png',
      links: [{ label: 'Paper Ballot Series', url: '/paper-ballots#series', icon: ImageIcon, testid: 'media-link-paper-ballots' }], all: null },
    { key: 'photos', icon: Camera, title: 'Photos', body: 'Our lakes, farms, villages and the people who make Ward 8 home.', image: '/api/uploads/ward8-aerial.jpg',
      links: [{ label: 'Beautiful Ward 8', url: '/gallery?album=ward8', icon: Camera, testid: 'media-link-beautiful-ward8' }], all: { label: 'All photos', url: '/gallery', testid: 'media-link-all-photos' } },
  ];

  return (
    <div data-testid="media-hub-page">
      <PageHeader pageKey="media" kicker="MEDIA" title="Steve's Videos, Posters and Photos" subtitle="Two Minutes with Steve, event recordings, the Paper Ballot poster series and photos of beautiful Ward 8." image={fileUrl('/api/uploads/ward8-aerial.jpg')} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <SectionHeader kicker="BROWSE" title="Three places to look" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="media-groups">
          {GROUPS.map(({ icon: Icon, ...g }) => (
            <Card key={g.key} className="overflow-hidden card-lift" data-testid={`media-group-${g.key}`}>
              <div className="relative h-40 bg-[#1a2744]">
                <img src={fileUrl(g.image)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-80" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1a2744] to-transparent" />
                <p className="absolute bottom-3 left-4 text-white font-black text-xl inline-flex items-center gap-2"><Icon className="h-5 w-5 text-[#C9A84C]" /> {g.title}</p>
              </div>
              <CardContent className="p-5">
                <p className="text-sm text-slate-600 leading-relaxed">{g.body}</p>
                <div className="mt-4 flex flex-col gap-2">
                  {g.links.map(({ icon: LIcon, ...l }) => (
                    <Button key={l.url} asChild variant="outline" className="h-11 justify-start border-slate-300 text-[#1a2744] font-bold bg-white btn-press" data-testid={l.testid}>
                      <Link to={l.url}><LIcon className="h-4 w-4 mr-2 text-[#24406f]" /> {l.label}</Link>
                    </Button>
                  ))}
                  {g.all && <Button asChild className="h-11 bg-[#1a2744] hover:bg-[#24406f] text-white font-bold btn-press" data-testid={g.all.testid}><Link to={g.all.url}>{g.all.label} <ArrowRight className="h-4 w-4 ml-1" /></Link></Button>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        <p className="mt-6 text-sm text-slate-500" data-testid="media-other-note">Looking for other candidates' forums and City Council streams? They are kept separate under <Link to="/videos?collection=other" className="underline font-semibold text-[#24406f]">Other CKL Election Videos</Link>.</p>
      </div>
    </div>
  );
}
